const API_BASE = import.meta.env.VITE_API_BASE;

// Cache mechanism
let userDataCache = null;
let userDataPromise = null;
const CACHE_DURATION = 2000; // 2 seconds

// Token management
export const getToken = () => localStorage.getItem('jwt');
export const setToken = (token) => {
  localStorage.setItem('jwt', token);
  // Clear cache when token changes
  userDataCache = null;
  userDataPromise = null;
  // Dispatch an event to notify components about token change
  window.dispatchEvent(new Event('auth-token-changed'));
};
export const removeToken = () => {
  localStorage.removeItem('jwt');
  // Clear cache when token is removed
  userDataCache = null;
  userDataPromise = null;
  // Dispatch an event to notify components about token removal
  window.dispatchEvent(new Event('auth-token-changed'));
};

// Auth headers
export const getAuthHeaders = () => {
  const token = getToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json;charset=UTF-8'
  };
};

// Fetch options with auth
export const getFetchOptions = (method = 'GET', body = null) => {
  const options = {
    method,
    headers: getAuthHeaders()
  };

  if (body) {
    options.body = JSON.stringify(body);
  }

  return options;
};

// Handle unauthorized access
export const handleUnauthorized = (navigate) => {
  removeToken();
  navigate('/login', { replace: true });
};

// Verify token and get user data
export const verifyToken = async () => {
  const token = getToken();
  if (!token) {
    userDataCache = null;
    userDataPromise = null;
    // Dispatch null user data
    window.dispatchEvent(new CustomEvent('user-data-changed', { detail: null }));
    return null;
  }

  // Return cached data if available and fresh
  if (userDataCache) {
    // Dispatch cached user data
    window.dispatchEvent(new CustomEvent('user-data-changed', { detail: userDataCache }));
    return userDataCache;
  }

  // If there's an ongoing request, wait for it
  if (userDataPromise) {
    const data = await userDataPromise;
    // Dispatch user data from promise
    window.dispatchEvent(new CustomEvent('user-data-changed', { detail: data }));
    return data;
  }

  try {
    // Create new promise for this request
    userDataPromise = fetch(`${API_BASE}/user`, {
      headers: getAuthHeaders()
    }).then(async (response) => {
      if (response.ok) {
        const data = await response.json();
        // Cache the result
        userDataCache = data;
        // Clear cache after duration
        setTimeout(() => {
          userDataCache = null;
        }, CACHE_DURATION);
        // Dispatch new user data
        window.dispatchEvent(new CustomEvent('user-data-changed', { detail: data }));
        return data;
      } else if (response.status === 401) {
        removeToken();
        // Dispatch null user data on unauthorized
        window.dispatchEvent(new CustomEvent('user-data-changed', { detail: null }));
        return null;
      }
      return null;
    });

    const result = await userDataPromise;
    userDataPromise = null; // Clear the promise
    return result;
  } catch (err) {
    console.error('Token verification failed:', err);
    userDataPromise = null; // Clear the promise on error
    // Dispatch null user data on error
    window.dispatchEvent(new CustomEvent('user-data-changed', { detail: null }));
    return null;
  }
};

// Handle API response
export const handleApiResponse = async (response, navigate) => {
  if (response.status === 403 || response.status === 401) {
    handleUnauthorized(navigate);
    throw new Error('Unauthorized access');
  }
  return response;
}; 